import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import AuroraLogo from "./AuroraLogo";
import { supabase } from "@/integrations/supabase/client";

const XIcon = ({
  className
}: {
  className?: string;
}) => <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true" role="img">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>;

interface SocialLink {
  id: string;
  platform: string;
  url: string;
  is_active: boolean;
}

const Footer = () => {
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  const hasFetched = useRef(false);
  
  useEffect(() => {
    // Only fetch once and defer until idle to not block initial paint
    if (hasFetched.current) return;
    hasFetched.current = true;
    
    const fetchSocialLinks = () => {
      supabase.from('social_links').select('*').eq('is_active', true).then(({ data }) => {
        if (data) setSocialLinks(data);
      });
    };
    
    // Use requestIdleCallback to defer non-critical fetch
    if ('requestIdleCallback' in window) {
      (window as Window).requestIdleCallback(fetchSocialLinks, { timeout: 2000 });
    } else {
      setTimeout(fetchSocialLinks, 100);
    }
  }, []);
  
  const xLink = socialLinks.find(link => link.platform === 'x');
  return <footer role="contentinfo" aria-label="Site footer" className="bg-[#0d0d0d] border-t border-white/10 py-[40px] overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-full">
        {/* Logo & Description */}
        <div className="mb-16">
          <Link to="/" className="flex items-center gap-2 group mb-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-md">
            <AuroraLogo size={22} />
            <span className="font-sans font-medium text-base text-white group-hover:text-primary transition-colors">
              AurØra
            </span>
          </Link>
          <p className="text-sm text-white/70 leading-relaxed max-w-[280px] font-mono">
            A new asset wrapper for property income, powered by on-chain infrastructure.
          </p>
        </div>

        {/* Navigation - All 4 columns on same line */}
        <nav className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 lg:gap-16 mb-16" aria-label="Footer navigation">
          {/* About Links */}
          <div id="footer-about" className="min-w-0">
            <h4 className="text-xs font-medium text-white mb-4 sm:mb-6 uppercase tracking-[0.2em]">ABOUT</h4>
            <div className="flex flex-col gap-2 sm:gap-3 text-xs sm:text-sm text-white/70">
              <Link to="/investment-philosophy" className="hover:text-white transition-colors focus-visible:text-white focus-visible:outline-none focus-visible:underline break-words">Investment Philosophy</Link>
              <Link to="/structure-discipline" className="hover:text-white transition-colors focus-visible:text-white focus-visible:outline-none focus-visible:underline break-words">Structure & Discipline</Link>
              <Link to="/stewardship-scale" className="hover:text-white transition-colors focus-visible:text-white focus-visible:outline-none focus-visible:underline break-words">Stewardship & Scale</Link>
              <Link to="/token-economics" className="hover:text-white transition-colors focus-visible:text-white focus-visible:outline-none focus-visible:underline break-words">Token Economics</Link>
            </div>
          </div>

          {/* Legal Links */}
          <div className="min-w-0">
            <h4 className="text-xs font-medium text-white mb-4 sm:mb-6 uppercase tracking-[0.2em]">LEGAL</h4>
            <div className="flex flex-col gap-2 sm:gap-3 text-xs sm:text-sm text-white/70">
              <Link to="/privacy" className="hover:text-white transition-colors focus-visible:text-white focus-visible:outline-none focus-visible:underline break-words">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors focus-visible:text-white focus-visible:outline-none focus-visible:underline break-words">Terms of Service</Link>
              <Link to="/cookies" className="hover:text-white transition-colors focus-visible:text-white focus-visible:outline-none focus-visible:underline break-words">Cookie Policy</Link>
              <Link to="/risk-disclosure" className="hover:text-white transition-colors focus-visible:text-white focus-visible:outline-none focus-visible:underline break-words">Risk Disclosure</Link>
            </div>
          </div>

          {/* Responsibility Links */}
          <div className="min-w-0">
            <h4 className="text-xs font-medium text-white mb-4 sm:mb-6 uppercase tracking-[0.2em]">RESPONSIBILITY</h4>
            <div className="flex flex-col gap-2 sm:gap-3 text-xs sm:text-sm text-white/70">
              <Link to="/esg" className="hover:text-white transition-colors focus-visible:text-white focus-visible:outline-none focus-visible:underline break-words">ESG Policy</Link>
              <Link to="/modern-slavery" className="hover:text-white transition-colors focus-visible:text-white focus-visible:outline-none focus-visible:underline break-words">Modern Slavery</Link>
              <Link to="/complaints-handling" className="hover:text-white transition-colors focus-visible:text-white focus-visible:outline-none focus-visible:underline break-words">Complaints</Link>
            </div>
          </div>

          {/* Contact */}
          <div className="min-w-0">
            <h4 className="text-xs font-medium text-white mb-4 sm:mb-6 uppercase tracking-[0.2em]">CONTACT</h4>
            <div className="flex flex-col gap-2 sm:gap-3 text-xs sm:text-sm text-white/70">
              <Link to="/contact" className="hover:text-white transition-colors focus-visible:text-white focus-visible:outline-none focus-visible:underline break-words">Get in Touch</Link>
            </div>
          </div>
        </nav>

        {/* Social Links - Separate Section */}
        {xLink && <div className="flex items-center gap-4 pb-10 mb-10 border-b border-white/10">
            <span className="text-xs font-medium text-white uppercase tracking-[0.2em]">FOLLOW US</span>
            <a href={xLink.url} target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors" aria-label="Follow us on X">
              <XIcon className="w-5 h-5" />
            </a>
          </div>}

        {/* Copyright */}
        <div className="pt-0">
          <p className="text-sm text-white/70 text-center">
            © {new Date().getFullYear()} AurØra. All rights reserved.
          </p>
        </div>
      </div>
    </footer>;
};
export default Footer;